import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import InfoRoundedIcon from "@mui/icons-material/InfoRounded"
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded"

import MainLayout from "../../layouts/MainLayout"
import { getStockAlerts } from "../../services/stockAlertService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


// =========================================================
// HELPERS
// =========================================================

function toNumber(
  value,
  fallback = 0
) {
  const parsed =
    Number(value)

  return Number.isFinite(parsed)
    ? parsed
    : fallback
}


function formatQuantity(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function getLocationTypeLabel(value) {
  if (
    value === "store"
  ) {
    return "Shop"
  }

  if (
    value === "warehouse"
  ) {
    return "Entrepôt"
  }

  return "-"
}


function getLocationTypeChip(value) {
  if (
    value === "store"
  ) {
    return (
      <Chip
        label="Shop"
        color="primary"
        size="small"
        variant="outlined"
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  if (
    value === "warehouse"
  ) {
    return (
      <Chip
        label="Entrepôt"
        color="secondary"
        size="small"
        variant="outlined"
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  return (
    <Chip
      label="-"
      size="small"
      variant="outlined"
      sx={{
        height: 24,
      }}
    />
  )
}


function getAlertConfig(value) {
  switch (value) {
    case "critical":
      return {
        label: "Critique",
        color: "error",
      }

    case "high":
      return {
        label: "Élevé",
        color: "warning",
      }

    case "medium":
      return {
        label: "Moyen",
        color: "info",
      }

    default:
      return {
        label: "Normal",
        color: "default",
      }
  }
}


function getAlertChip(value) {
  const config =
    getAlertConfig(value)

  return (
    <Chip
      label={
        config.label
      }
      color={
        config.color
      }
      size="small"
      sx={{
        height: 24,
        fontWeight: 900,
      }}
    />
  )
}


function getAlertRowClass(value) {
  if (value === "critical") {
    return "stock-alert-row stock-alert-row--critical"
  }

  if (value === "high") {
    return "stock-alert-row stock-alert-row--high"
  }

  if (value === "medium") {
    return "stock-alert-row stock-alert-row--medium"
  }

  return "stock-alert-row stock-alert-row--normal"
}


function getErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.35,
          sm: 1.5,
        },

        py: 1.15,

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.1,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            display: "grid",
            placeItems: "center",

            borderRadius: 2.5,

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.7,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 19,
                  sm: 21,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",

                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                minWidth: 0,

                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE CARD
// =========================================================

function AlertMobileCard({
  row,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        boxShadow:
          "0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,

          "&:last-child": {
            pb: 1.5,
          },
        }}
      >
        <Stack spacing={1.25}>
          <Box
            sx={{
              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "flex-start",

              gap: 1,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  overflowWrap:
                    "anywhere",
                }}
              >
                {row.product_name ||
                  "-"}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {row.product_code ||
                  "-"}
              </Typography>
            </Box>

            {getAlertChip(
              row.alert_level
            )}
          </Box>


          <Box
            sx={{
              display: "flex",

              flexWrap: "wrap",

              alignItems: "center",

              gap: 0.7,
            }}
          >
            {getLocationTypeChip(
              row.location_type
            )}

            <Typography
              variant="body2"
              fontWeight={800}
              color="#334155"
            >
              {row.location_name ||
                "-"}
            </Typography>
          </Box>


          {row.category_name ? (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Catégorie :{" "}
              <strong>
                {row.category_name}
              </strong>
            </Typography>
          ) : null}


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                "repeat(2, minmax(0, 1fr))",

              gap: 0.8,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 0.9,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Disponible
              </Typography>

              <Typography
                fontWeight={900}
                color={
                  toNumber(
                    row.available_quantity
                  ) <= 0
                    ? "error.main"
                    : "#0f172a"
                }
              >
                {formatQuantity(
                  row.available_quantity
                )}
              </Typography>
            </Paper>


            <Paper
              variant="outlined"
              sx={{
                p: 0.9,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Réappro. suggéré
              </Typography>

              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                {toNumber(
                  row.suggested_restock_qty
                ).toLocaleString(
                  "fr-FR",
                  {
                    maximumFractionDigits: 0,
                  }
                )}
              </Typography>
            </Paper>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function StockAlertsPage() {
  const theme =
    useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const [
    alerts,
    setAlerts,
  ] = useState([])

  const [
    summary,
    setSummary,
  ] = useState({
    critical_count: 0,
    high_count: 0,
    medium_count: 0,
    total_alerts: 0,
  })

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    locationTypeFilter,
    setLocationTypeFilter,
  ] = useState("")

  const [
    alertLevelFilter,
    setAlertLevelFilter,
  ] = useState("")

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize:
      ROWS_PER_PAGE,
  })

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message:
        String(
          message || ""
        ),
    })
  }


  const handleCloseSnackbar =
    () => {
      setSnackbar(
        (previous) => ({
          ...previous,
          open: false,
        })
      )
    }


  // =======================================================
  // LOAD
  // =======================================================

  const loadAlerts =
    async (
      customLocationType =
        locationTypeFilter
    ) => {
      try {
        setLoading(true)

        const data =
          await getStockAlerts(
            customLocationType
          )

        setAlerts(
          Array.isArray(
            data?.items
          )
            ? data.items
            : []
        )

        setSummary(
          data?.summary || {
            critical_count: 0,
            high_count: 0,
            medium_count: 0,
            total_alerts: 0,
          }
        )

        setPaginationModel({
          page: 0,
          pageSize:
            ROWS_PER_PAGE,
        })
      } catch (error) {
        console.error(
          "Error loading stock alerts:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les alertes de stock."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  useEffect(() => {
    loadAlerts(
      locationTypeFilter
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locationTypeFilter,
  ])


  // =======================================================
  // FILTER
  // =======================================================

  const filteredRows =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return alerts.filter(
        (row) => {
          const matchesSearch =
            keyword === "" ||
            [
              row.product_code,
              row.product_name,
              row.location_name,
              row.category_name,
              getLocationTypeLabel(
                row.location_type
              ),
            ].some(
              (value) =>
                String(
                  value || ""
                )
                  .toLowerCase()
                  .includes(
                    keyword
                  )
            )

          const matchesAlertLevel =
            alertLevelFilter === "" ||
            row.alert_level ===
              alertLevelFilter

          return (
            matchesSearch &&
            matchesAlertLevel
          )
        }
      )
    }, [
      alerts,
      search,
      alertLevelFilter,
    ])


  // =======================================================
  // PAGINATION
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,

        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })
    )
  }, [
    search,
    alertLevelFilter,
  ])


  const paginatedMobileRows =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return filteredRows.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredRows,
      paginationModel.page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredRows.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,

          pageSize:
            ROWS_PER_PAGE,
        })
      )
    }
  }, [
    filteredRows.length,
    paginationModel.page,
  ])


  // =======================================================
  // RESET
  // =======================================================

  const resetFilters =
    () => {
      setSearch("")
      setAlertLevelFilter("")

      if (
        locationTypeFilter !== ""
      ) {
        setLocationTypeFilter("")
      } else {
        setPaginationModel({
          page: 0,
          pageSize:
            ROWS_PER_PAGE,
        })
      }
    }


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "product",

          headerName:
            "Produit",

          minWidth: 205,

          flex: 1.25,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.18}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: "0.86rem",
                    fontWeight: 900,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.product_name ||
                    ""
                  }
                >
                  {params.row.product_name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    letterSpacing: "0.01em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.row.product_code ||
                    "-"}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "category_name",

          headerName:
            "Catégorie",

          minWidth: 175,

          flex: 1.05,

          renderCell:
            (params) => (
              <Typography
                sx={{
                  width: "100%",
                  color: "#475569",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  lineHeight: 1.28,
                  whiteSpace: "normal",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                }}

                title={
                  params.value ||
                  ""
                }
              >
                {params.value ||
                  "-"}
              </Typography>
            ),
        },


        {
          field:
            "location",

          headerName:
            "Emplacement",

          minWidth: 220,

          flex: 1.25,

          sortable: false,

          renderCell:
            (params) => (
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.9,
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  {getLocationTypeChip(
                    params.row.location_type
                  )}
                </Box>

                <Typography
                  sx={{
                    minWidth: 0,
                    color: "#334155",
                    fontSize: "0.83rem",
                    fontWeight: 800,
                    lineHeight: 1.28,
                    whiteSpace: "normal",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                  }}

                  title={
                    params.row.location_name ||
                    ""
                  }
                >
                  {params.row.location_name ||
                    "-"}
                </Typography>
              </Box>
            ),
        },


        {
          field:
            "available_quantity",

          headerName:
            "Disponible",

          width: 125,

          align: "right",

          headerAlign:
            "right",

          renderCell:
            (params) => {
              const quantity =
                toNumber(
                  params.value
                )

              const isZero =
                quantity <= 0

              return (
                <Box
                  sx={{
                    ml: "auto",
                    minWidth: 76,
                    px: 1,
                    py: 0.55,
                    borderRadius: 2,
                    textAlign: "right",
                    bgcolor: isZero
                      ? "rgba(220, 38, 38, 0.07)"
                      : "rgba(22, 163, 74, 0.07)",
                    border: "1px solid",
                    borderColor: isZero
                      ? "rgba(220, 38, 38, 0.16)"
                      : "rgba(22, 163, 74, 0.14)",
                  }}
                >
                  <Typography
                    sx={{
                      color: isZero
                        ? "#dc2626"
                        : "#166534",
                      fontSize: "0.84rem",
                      fontWeight: 900,
                      lineHeight: 1.2,
                    }}
                  >
                    {formatQuantity(
                      quantity
                    )}
                  </Typography>
                </Box>
              )
            },
        },


        {
          field:
            "alert_level",

          headerName:
            "Niveau",

          width: 120,

          renderCell:
            (params) => (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {getAlertChip(
                  params.value
                )}
              </Box>
            ),
        },


        {
          field:
            "suggested_restock_qty",

          headerName:
            "Réappro. suggéré",

          width: 145,

          align: "right",

          headerAlign:
            "right",

          renderCell:
            (params) => (
              <Stack
                spacing={0.05}

                alignItems="flex-end"

                sx={{
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: "0.86rem",
                    fontWeight: 900,
                    lineHeight: 1.2,
                  }}
                >
                  {toNumber(
                    params.value
                  ).toLocaleString(
                    "fr-FR",
                    {
                      maximumFractionDigits: 0,
                    }
                  )}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#94a3b8",
                    fontWeight: 700,
                    lineHeight: 1.1,
                  }}
                >
                  unité(s)
                </Typography>
              </Stack>
            ),
        },
      ],
      []
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              minHeight: 40,

              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 800,

              boxShadow: "none",
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,

              bgcolor: "#ffffff",
            },

          "& .MuiChip-root":
            {
              fontWeight: 800,
            },

          "& .MuiPaper-root":
            {
              backgroundImage:
                "none",
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            mb: 2,
          }}
        >
          <Typography
            variant="h4"

            sx={{
              fontWeight: 900,

              color: "#0f172a",

              lineHeight: 1.12,

              letterSpacing:
                "-0.025em",

              fontSize: {
                xs: "1.65rem",
                sm: "1.95rem",
                md: "2.1rem",
              },
            }}
          >
            Alertes & réapprovisionnement
          </Typography>


          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.4,

              lineHeight: 1.5,

              maxWidth: 760,
            }}
          >
            Suivi des stocks critiques, faibles et des besoins
            de ravitaillement.
          </Typography>
        </Box>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              md:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 1.1,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Critiques"

            value={
              summary.critical_count ||
              0
            }

            caption="Rupture ou niveau très bas"

            accent="#dc2626"

            background="rgba(220, 38, 38, 0.09)"

            icon={
              <ErrorRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Élevées"

            value={
              summary.high_count ||
              0
            }

            caption="Réapprovisionnement prioritaire"

            accent="#d97706"

            background="rgba(217, 119, 6, 0.09)"

            icon={
              <WarningAmberRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Moyennes"

            value={
              summary.medium_count ||
              0
            }

            caption="Niveau à surveiller"

            accent="#0284c7"

            background="rgba(2, 132, 199, 0.09)"

            icon={
              <InfoRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Total alertes"

            value={
              summary.total_alerts ||
              0
            }

            caption="Alertes détectées"

            accent="#7c3aed"

            background="rgba(124, 58, 237, 0.09)"

            icon={
              <NotificationsActiveRoundedIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(0, 1fr) 220px 220px 125px 135px",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"

              label="Recherche"

              placeholder="Produit, code, catégorie ou emplacement..."

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
            />


            <TextField
              select
              size="small"
              fullWidth

              label="Type de lieu"

              value={
                locationTypeFilter
              }

              onChange={(
                event
              ) =>
                setLocationTypeFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="store">
                Shop
              </MenuItem>

              <MenuItem value="warehouse">
                Entrepôt
              </MenuItem>
            </TextField>


            <TextField
              select
              size="small"
              fullWidth

              label="Niveau d’alerte"

              value={
                alertLevelFilter
              }

              onChange={(
                event
              ) =>
                setAlertLevelFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="critical">
                Critique
              </MenuItem>

              <MenuItem value="high">
                Élevé
              </MenuItem>

              <MenuItem value="medium">
                Moyen
              </MenuItem>
            </TextField>


            <Button
              variant="contained"
              disableElevation

              disabled={
                loading
              }

              onClick={() =>
                loadAlerts(
                  locationTypeFilter
                )
              }

              sx={{
                width: "100%",
                minWidth: 0,
                whiteSpace:
                  "nowrap",
              }}
            >
              Actualiser
            </Button>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              sx={{
                width: "100%",
                minWidth: 0,
                whiteSpace:
                  "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des alertes stock
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Produits à surveiller et quantités de
                réapprovisionnement suggérées
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredRows.length} alerte${
                filteredRows.length >
                1
                  ? "s"
                  : ""
              }`}
            />
          </Box>


          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflowX: "auto",

                bgcolor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  minWidth: 1030,
                }}
              >
                <DataGrid
                  autoHeight

                  rows={
                    filteredRows
                  }

                  columns={
                    columns
                  }

                  loading={
                    loading
                  }

                  disableRowSelectionOnClick

                  disableColumnSelector

                  pagination

                  paginationMode="client"

                  paginationModel={
                    paginationModel
                  }

                  onPaginationModelChange={(
                    model
                  ) =>
                    setPaginationModel({
                      page:
                        model.page,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  }

                  pageSizeOptions={[
                    ROWS_PER_PAGE,
                  ]}

                  rowHeight={68}

                  columnHeaderHeight={50}

                  getRowClassName={(
                    params
                  ) =>
                    getAlertRowClass(
                      params.row.alert_level
                    )
                  }

                  localeText={{
                    noRowsLabel:
                      "Aucune alerte de stock",

                    noResultsOverlayLabel:
                      "Aucun résultat",

                    MuiTablePagination:
                      {
                        labelRowsPerPage:
                          "",

                        labelDisplayedRows:
                          ({
                            from,
                            to,
                            count,
                          }) =>
                            `${from}–${to} sur ${count}`,
                      },
                  }}

                  sx={{
                    border: 0,

                    width: "100%",

                    color: "#334155",

                    fontSize: "0.83rem",

                    bgcolor:
                      "#ffffff",

                    "& .MuiDataGrid-columnHeaders":
                      {
                        bgcolor:
                          "#f8fafc",

                        borderBottom:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiDataGrid-columnHeader":
                      {
                        bgcolor:
                          "#f8fafc",

                        px: 1.25,

                        borderRight:
                          "1px solid rgba(226, 232, 240, 0.60)",
                      },

                    "& .MuiDataGrid-columnHeader:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeaderTitle":
                      {
                        color:
                          "#334155",

                        fontSize:
                          "0.76rem",

                        fontWeight: 900,

                        letterSpacing:
                          "0.025em",
                      },

                    "& .MuiDataGrid-columnSeparator":
                      {
                        color:
                          "rgba(148, 163, 184, 0.18)",
                      },

                    "& .MuiDataGrid-cell":
                      {
                        px: 1.25,

                        minWidth: 0,

                        display: "flex",

                        alignItems:
                          "center",

                        overflow:
                          "hidden",

                        borderBottom:
                          "1px solid #edf2f7",

                        borderRight:
                          "1px solid rgba(241, 245, 249, 0.85)",
                      },

                    "& .MuiDataGrid-cell:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .stock-alert-row":
                      {
                        bgcolor:
                          "#ffffff",

                        transition:
                          "background-color 0.16s ease, box-shadow 0.16s ease",
                      },

                    "& .stock-alert-row--critical":
                      {
                        boxShadow:
                          "inset 4px 0 0 #ef4444",

                        bgcolor:
                          "rgba(254, 242, 242, 0.38)",
                      },

                    "& .stock-alert-row--high":
                      {
                        boxShadow:
                          "inset 4px 0 0 #f59e0b",

                        bgcolor:
                          "rgba(255, 251, 235, 0.42)",
                      },

                    "& .stock-alert-row--medium":
                      {
                        boxShadow:
                          "inset 4px 0 0 #0ea5e9",

                        bgcolor:
                          "rgba(240, 249, 255, 0.46)",
                      },

                    "& .stock-alert-row--normal":
                      {
                        boxShadow:
                          "inset 4px 0 0 #cbd5e1",
                      },

                    "& .stock-alert-row:hover":
                      {
                        bgcolor:
                          "#f8fbff !important",

                        boxShadow:
                          "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                      },

                    "& .MuiDataGrid-footerContainer":
                      {
                        minHeight: 52,

                        bgcolor:
                          "#fbfdff",

                        borderTop:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiTablePagination-toolbar":
                      {
                        minHeight: 52,

                        px: 1.25,
                      },

                    "& .MuiTablePagination-displayedRows":
                      {
                        color:
                          "#475569",

                        fontSize:
                          "0.78rem",

                        fontWeight: 800,
                      },

                    "& .MuiTablePagination-selectLabel":
                      {
                        display: "none",
                      },

                    "& .MuiTablePagination-input":
                      {
                        display: "none",
                      },

                    "& .MuiDataGrid-virtualScroller":
                      {
                        overflowX:
                          "auto",
                      },

                    "& .MuiDataGrid-scrollbar--horizontal":
                      {
                        height:
                          "8px",
                      },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: {
                  xs: 1.1,
                  sm: 1.25,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des alertes...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucune alerte trouvée.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileRows.map(
                    (row) => (
                      <AlertMobileCard
                        key={
                          row.id
                        }

                        row={
                          row
                        }
                      />
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"

                count={
                  filteredRows.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>


        {/* =================================================
            SNACKBAR
        ================================================= */}

        <Snackbar
          open={
            snackbar.open
          }

          autoHideDuration={
            3500
          }

          onClose={
            handleCloseSnackbar
          }

          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Alert
            onClose={
              handleCloseSnackbar
            }

            severity={
              snackbar.severity
            }

            variant="filled"

            sx={{
              width: "100%",

              borderRadius: 3,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  )
}


export default StockAlertsPage
